import React, {
   useState,
   useEffect,
   useRef,
   memo,
   useMemo,
   useContext,
} from "react";
import { getCurrentCDSToken } from "./Auth";
import { useUser } from "./User";
import { AudioContext } from "./AudioPlayer";
import { useTheme } from "./ThemeContext"; // Import the theme hook
import config from "./config";
import AudiobookPromptSelector from "./AudiobookPromptSelector";

// Immediately invoked function to inject animation styles just once
// This ensures the animation doesn't restart on theme changes
if (typeof document !== "undefined") {
   // Check if style already exists to avoid duplicates
   if (!document.getElementById("audiobook-spinner-styles")) {
      const styleEl = document.createElement("style");
      styleEl.id = "audiobook-spinner-styles";
      styleEl.textContent = `
         @keyframes continuousSpinAnimation {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
         }
         .audiobook-generation-spinner {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            border: 2px solid;
            border-top-color: transparent !important;
            animation: continuousSpinAnimation 1s linear infinite;
         }
      `;
      document.head.appendChild(styleEl);
   }
}

// Simple spinner component that uses the global styles
const GenerationSpinner = memo(({ isLight }) => {
   return (
      <div
         className="audiobook-generation-spinner"
         style={{
            borderColor: isLight ? "#9ca3af" : "#6b7280",
         }}
      />
   );
});

// Get styles based on current theme
const getStyles = (theme) => {
   const isLight = theme === "light";
   return {
      container: {
         maxWidth: "1200px",
         margin: "0 auto",
         padding: "3rem",
      },
      generatingIndicator: {
         color: isLight ? "#9ca3af" : "#d1d5db",
         fontSize: "0.7rem",
         fontStyle: "italic",
         margin: "2px 0 0 0",
      },
      pageTitle: {
         color: "var(--text-primary)",
         fontWeight: "700",
         margin: "0 2rem 1rem 0",
         paddingTop: "1rem",
      },
      loadingContainer: {
         display: "flex",
         alignItems: "center",
         justifyContent: "center",
         padding: "3rem",
         backgroundColor: isLight
            ? "var(--bg-secondary)"
            : "rgba(34, 34, 34, 0.98)",
         borderRadius: "0.5rem",
         border: `1px solid ${isLight ? "var(--border-color)" : "#333"}`,
         boxShadow: isLight
            ? "0 4px 8px rgba(0, 0, 0, 0.1)"
            : "0 4px 8px rgba(0, 0, 0, 0.3)",
         margin: "1rem 0 2rem 0",
      },
      loadingText: {
         color: isLight ? "var(--text-muted)" : "#999",
      },
      errorContainer: {
         padding: "1.5rem",
         backgroundColor: isLight
            ? "rgba(239, 68, 68, 0.05)"
            : "rgba(239, 68, 68, 0.1)",
         borderRadius: "0.5rem",
         marginBottom: "1.5rem",
         border: "1px solid rgba(239, 68, 68, 0.3)",
      },
      errorText: {
         color: "#ef4444",
         marginBottom: "1rem",
      },
      tryAgainButton: {
         backgroundColor: "#ef4444",
         color: "white",
         padding: "0.5rem 1rem",
         borderRadius: "0.375rem",
         border: "none",
         cursor: "pointer",
      },
      emptyLibraryContainer: {
         backgroundColor: isLight
            ? "var(--bg-secondary)"
            : "rgba(34, 34, 34, 0.98)",
         borderRadius: "0.5rem",
         border: `1px solid ${isLight ? "var(--border-color)" : "#333"}`,
         padding: "2rem",
         textAlign: "center",
         boxShadow: isLight
            ? "0 4px 8px rgba(0, 0, 0, 0.1)"
            : "0 4px 8px rgba(0, 0, 0, 0.3)",
      },
      emptyIconContainer: {
         width: "64px",
         height: "64px",
         borderRadius: "50%",
         backgroundColor: isLight
            ? "rgba(75, 85, 99, 0.05)"
            : "rgba(75, 85, 99, 0.1)",
         display: "flex",
         alignItems: "center",
         justifyContent: "center",
         margin: "0 auto 1.5rem auto",
         border: isLight
            ? "1px solid rgba(75, 85, 99, 0.1)"
            : "1px solid rgba(75, 85, 99, 0.2)",
      },
      emptyIcon: {
         color: isLight ? "var(--text-muted)" : "#999",
         fontSize: "2rem",
      },
      emptyTitle: {
         color: "var(--text-primary)",
         fontSize: "1.25rem",
         fontWeight: "500",
         marginBottom: "0.75rem",
      },
      emptyText: {
         color: isLight ? "var(--text-muted)" : "#999",
         fontSize: "0.9rem",
         marginBottom: "1rem",
      },
      // LibraryItem styles
      libraryItem: (showPublishDialog, isCurrentlySelected) => ({
         marginBottom: "1rem",
         display: "flex",
         alignItems: "center",
         justifyContent: "space-between",
         padding: "0.5rem",
         borderBottom: showPublishDialog
            ? "none"
            : getBorderStyle(isLight, showPublishDialog, isCurrentlySelected),
         position: "relative",
         backgroundColor: getBackgroundColor(
            isLight,
            showPublishDialog,
            isCurrentlySelected
         ),
         borderRadius: showPublishDialog || isCurrentlySelected ? "4px" : "0",
         boxShadow: getBoxShadow(
            isLight,
            showPublishDialog,
            isCurrentlySelected
         ),
         transition: "all 0.2s ease",
      }),
      playButton: (isCurrentlySelected, isGenerating = false) => {
         const baseStyle = {
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            backgroundColor: isLight ? "var(--bg-tertiary)" : "#111",
            border: isCurrentlySelected
               ? `2px solid ${isLight ? "var(--accent-primary)" : "#10b981"}`
               : `2px solid ${isLight ? "var(--border-color)" : "#333"}`,
            color: isLight ? "var(--text-primary)" : "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "1rem",
            cursor: "pointer",
         };

         if (isGenerating) {
            return {
               ...baseStyle,
               opacity: 0.7,
               cursor: "default",
               backgroundColor: isLight ? "#f3f4f6" : "#2d3748",
               borderColor: isLight ? "#d1d5db" : "#4b5563",
               color: isLight ? "#9ca3af" : "#6b7280",
            };
         }
         return baseStyle;
      },
      spinnerAnimation: {
         width: "16px",
         height: "16px",
         borderRadius: "50%",
         border: `2px solid ${isLight ? "#9ca3af" : "#6b7280"}`,
         borderTopColor: "transparent",
         animation: "continuousSpinAnimation 1s linear infinite",
      },
      imagePlaceholder: {
         width: "56px",
         height: "56px",
         backgroundColor: isLight ? "var(--bg-tertiary)" : "#222",
         borderRadius: "4px",
         marginRight: "1rem",
      },
      bookImage: {
         width: "56px",
         height: "56px",
         objectFit: "cover",
         borderRadius: "4px",
         marginRight: "1rem",
      },
      bookTitle: (isCurrentlySelected) => ({
         margin: 0,
         color: "var(--text-primary)",
         fontSize: "1rem",
         fontWeight: isCurrentlySelected ? "500" : "normal",
      }),
      bookChapter: {
         margin: 0,
         color: isLight ? "var(--text-muted)" : "#999",
         fontSize: "0.875rem",
      },
      publishContainer: {
         display: "flex",
         flexDirection: "column",
         alignItems: "center",
         marginRight: "1rem",
         width: "36px" /* Fixed width for better alignment */,
      },
      publishedButton: {
         backgroundColor: isLight ? "var(--accent-primary)" : "#10b981", // Green color for published
         width: "36px",
         height: "36px",
         borderRadius: "50%",
         display: "flex",
         alignItems: "center",
         justifyContent: "center",
         marginBottom: "4px",
         color: "white",
      },
      publishButton: (showPublishDialog, isGenerating) => ({
         backgroundColor: showPublishDialog
            ? isLight
               ? "var(--accent-primary)"
               : "#10b981" // Green when active
            : isGenerating
            ? isLight
               ? "rgba(156, 163, 175, 0.3)" // Light gray for disabled in light mode
               : "rgba(75, 85, 99, 0.3)" // Dark gray for disabled in dark mode
            : isLight
            ? "var(--bg-tertiary)"
            : "#444",
         width: "36px",
         height: "36px",
         borderRadius: "50%",
         border: "none",
         color: isGenerating
            ? isLight
               ? "rgba(255, 255, 255, 0.5)"
               : "rgba(255, 255, 255, 0.4)"
            : "white",
         display: "flex",
         alignItems: "center",
         justifyContent: "center",
         cursor: isGenerating ? "not-allowed" : "pointer",
         marginBottom: "4px",
         transition: "background-color 0.2s ease",
         opacity: isGenerating ? 0.6 : 1,
      }),
      publishText: (showPublishDialog, isGenerating) => ({
         color: showPublishDialog
            ? isLight
               ? "var(--accent-primary)"
               : "#10b981"
            : isGenerating
            ? isLight
               ? "rgba(156, 163, 175, 0.7)" // Muted text for disabled in light mode
               : "rgba(156, 163, 175, 0.5)" // Muted text for disabled in dark mode
            : isLight
            ? "var(--text-muted)"
            : "#999",
         fontSize: "0.75rem",
         fontWeight: showPublishDialog ? "600" : "normal",
         opacity: isGenerating ? 0.7 : 1,
      }),
      optionsButton: {
         background: "none",
         border: "none",
         color: "var(--text-primary)",
         fontSize: "1.5rem",
         cursor: "pointer",
         padding: "0.5rem",
         display: "flex",
         alignItems: "center",
         justifyContent: "center",
      },
      menuContainer: {
         position: "absolute",
         top: "100%",
         right: 0,
         backgroundColor: isLight
            ? "var(--bg-elevated)"
            : "rgba(34, 34, 34, 0.98)",
         border: `1px solid ${isLight ? "var(--border-color)" : "#444"}`,
         borderRadius: "4px",
         boxShadow: isLight
            ? "0 4px 8px rgba(0, 0, 0, 0.15)"
            : "0 4px 8px rgba(0, 0, 0, 0.3)",
         zIndex: 10,
         width: "150px",
         overflow: "hidden",
      },
      menuButton: {
         display: "block",
         width: "100%",
         padding: "0.75rem 1rem",
         textAlign: "left",
         backgroundColor: "transparent",
         border: "none",
         color: "#ef4444", // Red color for delete
         cursor: "pointer",
         fontSize: "0.875rem",
         transition: "background-color 0.2s ease",
      },
      menuButtonHover: {
         backgroundColor: isLight
            ? "rgba(0, 0, 0, 0.05)"
            : "rgba(255, 255, 255, 0.05)",
      },
      publishDialogContainer: {
         position: "absolute",
         top: "100%",
         left: 0,
         right: 0,
         marginTop: "0.5rem",
         borderRadius: "4px",
         border: `1px solid ${
            isLight ? "rgba(16, 185, 129, 0.2)" : "rgba(16, 185, 129, 0.3)"
         }`, // Green border
         padding: "1rem",
         width: "100%",
         textAlign: "right",
         backgroundColor: isLight
            ? "var(--bg-elevated)"
            : "rgba(34, 34, 34, 0.98)",
         zIndex: 5,
         boxShadow: isLight
            ? "0 4px 8px rgba(0, 0, 0, 0.1)"
            : "0 4px 8px rgba(0, 0, 0, 0.3)",
      },
      publishSuccessContainer: {
         textAlign: "center",
         color: isLight ? "var(--accent-primary)" : "#10b981", // Success green
         padding: "0.5rem",
      },
      publishSuccessIcon: {
         fontSize: "24px",
         marginBottom: "0.5rem",
      },
      publishSuccessText: {
         margin: 0,
      },
      publishDialogTitle: {
         margin: "0 0 1rem 0",
         color: "var(--text-primary)",
         textAlign: "left",
      },
      publishDialogText: {
         margin: "0 0 1rem 0",
         color: isLight ? "var(--text-muted)" : "#aaa",
         textAlign: "left",
         fontSize: "0.875rem",
      },
      publishErrorMessage: {
         color: "#ef4444",
         backgroundColor: isLight
            ? "rgba(239, 68, 68, 0.05)"
            : "rgba(239, 68, 68, 0.1)",
         padding: "0.5rem",
         marginBottom: "1rem",
         borderRadius: "4px",
         textAlign: "left",
         fontSize: "0.875rem",
      },
      cancelButton: {
         backgroundColor: "transparent",
         color: isLight ? "var(--text-muted)" : "#aaa",
         border: `1px solid ${isLight ? "var(--border-color)" : "#555"}`,
         borderRadius: "4px",
         padding: "0.5rem 1rem",
         marginRight: "0.5rem",
         cursor: "pointer",
         fontSize: "0.875rem",
      },
      confirmButton: {
         backgroundColor: isLight ? "var(--accent-primary)" : "#10b981",
         color: "white",
         border: "none",
         borderRadius: "4px",
         padding: "0.5rem 1rem",
         cursor: "pointer",
         fontSize: "0.875rem",
         fontWeight: "600",
      },
      // Delete modal styles
      modalOverlay: {
         position: "fixed",
         top: 0,
         left: 0,
         right: 0,
         bottom: 0,
         backgroundColor: "rgba(0, 0, 0, 0.75)",
         display: "flex",
         justifyContent: "center",
         alignItems: "center",
         zIndex: 100,
      },
      modalContainer: {
         backgroundColor: isLight
            ? "var(--bg-elevated)"
            : "rgba(34, 34, 34, 0.98)",
         borderRadius: "8px",
         boxShadow: isLight
            ? "0 4px 24px rgba(0, 0, 0, 0.25)"
            : "0 4px 24px rgba(0, 0, 0, 0.5)",
         padding: "1.5rem",
         width: "90%",
         maxWidth: "400px",
         zIndex: 101,
         border: `1px solid ${isLight ? "var(--border-color)" : "#444"}`,
      },
      modalIconContainer: {
         marginBottom: "1.5rem",
         textAlign: "center",
      },
      modalIconCircle: {
         width: "50px",
         height: "50px",
         borderRadius: "50%",
         backgroundColor: isLight
            ? "rgba(239, 68, 68, 0.05)"
            : "rgba(239, 68, 68, 0.1)",
         display: "flex",
         alignItems: "center",
         justifyContent: "center",
         margin: "0 auto 1rem auto",
      },
      modalIcon: {
         color: "#ef4444",
         fontSize: "1.5rem",
      },
      modalTitle: {
         color: "var(--text-primary)",
         margin: "0 0 0.5rem 0",
         fontWeight: "500",
      },
      modalText: {
         color: isLight ? "var(--text-muted)" : "#aaa",
         marginBottom: "1.5rem",
         textAlign: "center",
         fontSize: "0.875rem",
      },
      modalButtonContainer: {
         display: "flex",
         justifyContent: "center",
         gap: "0.75rem",
      },
      modalCancelButton: {
         backgroundColor: "transparent",
         color: isLight ? "var(--text-muted)" : "#aaa",
         border: `1px solid ${isLight ? "var(--border-color)" : "#555"}`,
         borderRadius: "4px",
         padding: "0.75rem 1.25rem",
         cursor: "pointer",
         fontSize: "0.875rem",
         fontWeight: "normal",
      },
      modalDeleteButton: {
         backgroundColor: "#ef4444",
         color: "white",
         border: "none",
         borderRadius: "4px",
         padding: "0.75rem 1.25rem",
         cursor: "pointer",
         fontSize: "0.875rem",
         fontWeight: "600",
      },
   };
};

// Helper functions for dynamic styles
const getBackgroundColor = (
   isLight,
   showPublishDialog,
   isCurrentlySelected
) => {
   if (showPublishDialog) {
      // Green highlight for publish confirmation (takes priority)
      return isLight ? "rgba(16, 185, 129, 0.05)" : "rgba(16, 185, 129, 0.1)";
   } else if (isCurrentlySelected) {
      // Gray highlight for currently playing items
      return isLight ? "rgba(75, 85, 99, 0.05)" : "rgba(75, 85, 99, 0.1)";
   } else {
      return "transparent";
   }
};

const getBorderStyle = (isLight, showPublishDialog, isCurrentlySelected) => {
   if (showPublishDialog) {
      // Green border for publish confirmation
      return `1px solid ${
         isLight ? "rgba(16, 185, 129, 0.2)" : "rgba(16, 185, 129, 0.3)"
      }`;
   } else if (isCurrentlySelected) {
      // Dark gray border for currently playing items
      return `1px solid ${
         isLight ? "rgba(75, 85, 99, 0.1)" : "rgba(75, 85, 99, 0.2)"
      }`;
   } else {
      return `1px solid ${isLight ? "var(--border-color)" : "#333"}`;
   }
};

const getBoxShadow = (isLight, showPublishDialog, isCurrentlySelected) => {
   if (showPublishDialog) {
      return `0 0 0 1px ${
         isLight ? "rgba(16, 185, 129, 0.1)" : "rgba(16, 185, 129, 0.2)"
      }`;
   } else if (isCurrentlySelected) {
      return `0 0 0 1px ${
         isLight ? "rgba(75, 85, 99, 0.05)" : "rgba(75, 85, 99, 0.1)"
      }`;
   } else {
      return "none";
   }
};

// Updated LibraryItem Component with modified spinner implementation
const LibraryItem = memo(
   ({
      book,
      onRemove,
      onPublish,
      onListen,
      showPublishDialog,
      onPublishDialogToggle,
      theme,
   }) => {
      const styles = getStyles(theme);
      const isLight = theme === "light";

      const [publishSuccess, setPublishSuccess] = useState(false);
      const [publishError, setPublishError] = useState(null);
      const [imageSrc, setImageSrc] = useState(null);
      const [loading, setLoading] = useState(true);
      const [error, setError] = useState(false);
      const [showMenu, setShowMenu] = useState(false);
      const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
      const userId = localStorage.getItem("userId");
      const imageLoadedRef = useRef(false);
      const menuRef = useRef(null);
      const deleteModalRef = useRef(null);

      // Get the audio context to check if this book is currently playing
      const audioContext = useContext(AudioContext);

      // Determine if this book is the one currently selected
      const isCurrentlySelected = useMemo(() => {
         return (
            audioContext &&
            audioContext.currentBook &&
            audioContext.currentBook.uuid === book.uuid
         );
      }, [audioContext, book.uuid]);

      // Determine if this book is actually playing (not just selected)
      const isActuallyPlaying = useMemo(() => {
         return (
            isCurrentlySelected &&
            audioContext &&
            audioContext.playbackState &&
            audioContext.playbackState.isPlaying
         );
      }, [isCurrentlySelected, audioContext]);

      // Check if the book is still generating
      const isGenerating = useMemo(() => {
         // If generation_status exists and is not 'completed' or similar values
         return (
            book.generation_status &&
            !["completed", "done", "finished", "ready"].includes(
               book.generation_status.toLowerCase()
            )
         );
      }, [book.generation_status]);

      // Close menu when clicking outside
      useEffect(() => {
         const handleOutsideClick = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
               setShowMenu(false);
            }

            // Also handle outside clicks for delete modal
            if (
               deleteModalRef.current &&
               !deleteModalRef.current.contains(event.target)
            ) {
               setShowDeleteConfirm(false);
            }
         };

         if (showMenu || showDeleteConfirm) {
            document.addEventListener("mousedown", handleOutsideClick);
         }

         return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
         };
      }, [showMenu, showDeleteConfirm]);

      useEffect(() => {
         let isMounted = true;

         const loadImage = async () => {
            // Skip loading if we already have the image
            if (imageLoadedRef.current && imageSrc) {
               return;
            }

            try {
               let cdsToken = null;
               if (!book.is_public) {
                  try {
                     cdsToken = await getCurrentCDSToken();
                  } catch (err) {
                     console.error("Failed to get CDS token:", err);
                     throw new Error("Failed to authenticate with CDS");
                  }
               }

               const imageUrl = `${config.cdsUrl}/user/audiobooks/${book.uuid}/cover?size=128x128&user_id=${userId}`;
               const headers = {
                  Accept: "image/jpeg,image/png,*/*",
                  Origin: config.primaryUrl,
               };

               if (cdsToken) {
                  headers["Authorization"] = JSON.stringify(cdsToken);
               }

               const response = await fetch(imageUrl, {
                  method: "GET",
                  headers,
                  credentials: "include",
                  mode: "cors",
               });

               if (!response.ok) {
                  throw new Error(`Failed to load image: ${response.status}`);
               }

               const blob = await response.blob();
               const objectUrl = URL.createObjectURL(blob);

               if (isMounted) {
                  setImageSrc(objectUrl);
                  setLoading(false);
                  setError(false);
                  imageLoadedRef.current = true; // Mark that we've loaded the image
               }
            } catch (err) {
               console.error("Error loading image:", err);
               if (isMounted) {
                  setError(true);
                  setLoading(false);
               }
            }
         };

         loadImage();

         return () => {
            isMounted = false;
            if (imageSrc) {
               URL.revokeObjectURL(imageSrc);
            }
         };
      }, [book.uuid, imageSrc, book.is_public, userId]);

      const handlePublish = async () => {
         try {
            setPublishError(null);

            const token = localStorage.getItem("token");
            if (!token) {
               throw new Error(
                  "Authentication token missing. Please login again."
               );
            }

            const response = await fetch(
               `/api/user/library/publish/${book.uuid}`,
               {
                  method: "POST",
                  headers: {
                     Authorization: `Bearer ${token}`,
                     "Content-Type": "application/json",
                  },
               }
            );

            if (!response.ok) {
               let errorDetails = "Failed to publish audiobook";
               try {
                  const errorResponse = await response.json();
                  if (errorResponse.message) {
                     errorDetails = errorResponse.message;
                  }
               } catch (e) {
                  errorDetails = `Error (${response.status}): ${response.statusText}`;
               }

               throw new Error(errorDetails);
            }

            setPublishSuccess(true);

            setTimeout(() => {
               // Notify parent to close dialog
               onPublishDialogToggle(null);
               setPublishSuccess(false);

               if (typeof onPublish === "function") {
                  onPublish(book.uuid);
               }
            }, 1500);
         } catch (error) {
            console.error("Error publishing audiobook:", error);
            setPublishError(
               error.message || "Error publishing audiobook. Please try again."
            );
         }
      };

      // Handle clicking the play/pause button
      const handlePlayPauseClick = () => {
         // Prevent playback if book is still generating
         if (isGenerating) {
            console.log("Book is still generating, playback not available");
            return;
         }

         if (
            isActuallyPlaying &&
            audioContext &&
            audioContext.togglePlayPause
         ) {
            // If it's playing, just toggle to pause
            audioContext.togglePlayPause();
         } else {
            // Otherwise load and play the book
            onListen(book);
         }
      };

      // Handle toggle for the options menu
      const handleMenuToggle = (e) => {
         e.stopPropagation(); // Prevent event bubbling
         setShowMenu(!showMenu);
      };

      // Handle delete click - now opens the confirmation modal instead of using window.confirm
      const handleDeleteClick = (e) => {
         e.stopPropagation(); // Prevent event bubbling
         setShowMenu(false); // Close the menu
         setShowDeleteConfirm(true); // Show the delete confirmation modal
      };

      // Handle confirming deletion
      const confirmDelete = () => {
         if (typeof onRemove === "function") {
            onRemove(book.uuid);
         }
         setShowDeleteConfirm(false); // Close the modal
      };

      // Handle canceling deletion
      const cancelDelete = () => {
         setShowDeleteConfirm(false); // Close the modal
      };

      // Earth SVG Icon for unpublished state (publish button)
      const EarthSvgIcon = () => (
         <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
         >
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M2 12h20"></path>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
         </svg>
      );

      // Earth SVG Icon for published state with visual indicator
      const PublishedEarthSvgIcon = () => (
         <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
         >
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M2 12h20"></path>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
            {/* Added visual elements to indicate published status */}
            <path
               d="M18 8L10 16L6 12"
               stroke={isLight ? "#fff" : "#fff"}
               strokeWidth="2.5"
               strokeLinecap="round"
               strokeLinejoin="round"
            />
         </svg>
      );

      // Generate style for play button based on generation status
      const getPlayButtonStyle = () => {
         // Base style
         const baseStyle = styles.playButton(isCurrentlySelected);

         // Add disabled styling if book is generating
         if (isGenerating) {
            return {
               ...baseStyle,
               opacity: 0.7,
               cursor: "default",
               backgroundColor: isLight ? "#f3f4f6" : "#2d3748",
               borderColor: isLight ? "#d1d5db" : "#4b5563",
               color: isLight ? "#9ca3af" : "#6b7280",
            };
         }

         return baseStyle;
      };

      const isFirstChapterGenerating = useMemo(() => {
         // First determine if the book is generating at all
         const bookIsGenerating =
            book.generation_status &&
            !["completed", "done", "finished", "ready"].includes(
               book.generation_status.toLowerCase()
            );

         // Then check if the first chapter is ready
         const firstChapterGenerated =
            book.num_generated_chapters && book.num_generated_chapters >= 1;
         return bookIsGenerating && !firstChapterGenerated;
      }, [book.generation_status, book.num_generated_chapters]);

      return (
         <div
            style={styles.libraryItem(showPublishDialog, isCurrentlySelected)}
         >
            {/* Play/Pause button and image */}
            <div style={{ display: "flex", alignItems: "center" }}>
               <button
                  onClick={handlePlayPauseClick}
                  style={getPlayButtonStyle()}
                  title={isGenerating ? "Generation in progress..." : "Play"}
                  disabled={isGenerating}
               >
                  {isGenerating ? (
                     // Use the new spinner component with global styles
                     <GenerationSpinner isLight={isLight} />
                  ) : isActuallyPlaying ? (
                     <span style={{ fontSize: "18px", lineHeight: 1 }}>❚❚</span>
                  ) : (
                     <span>▶</span>
                  )}
               </button>
               {loading ? (
                  <div style={styles.imagePlaceholder}></div>
               ) : error ? (
                  <div style={styles.imagePlaceholder}></div>
               ) : (
                  <img
                     src={imageSrc || "/api/placeholder/56/56"}
                     alt={`${book.title || "Book"} cover`}
                     style={styles.bookImage}
                  />
               )}

               {/* Title and chapter info */}
               <div>
                  <h3 style={styles.bookTitle(isCurrentlySelected)}>
                     {book.title || "TBD"}
                  </h3>
                  <p style={styles.bookChapter}>
                     Chapter 1 of {book.num_total_chapters || "?"}
                  </p>
                  {/* Add generation status indicator */}
                  {isGenerating && (
                     <p style={styles.generatingIndicator}>
                        Generation in progress...
                     </p>
                  )}
               </div>
            </div>

            {/* Right side with action icons */}
            <div style={{ display: "flex", alignItems: "center" }}>
               <div style={styles.publishContainer}>
                  {/* Display different buttons based on publish status, but with consistent structure */}
                  {book.is_public ? (
                     /* Published state with Earth icon */
                     <>
                        <div style={styles.publishedButton}>
                           <PublishedEarthSvgIcon />
                        </div>
                        <span style={styles.publishedText}>published</span>
                     </>
                  ) : (
                     /* Unpublished state - UPDATED TO HANDLE DISABLED STATE */
                     <>
                        <button
                           onClick={() =>
                              !isFirstChapterGenerating &&
                              onPublishDialogToggle(book.uuid)
                           }
                           style={styles.publishButton(
                              showPublishDialog,
                              isFirstChapterGenerating
                           )}
                           disabled={isFirstChapterGenerating}
                           title={
                              isFirstChapterGenerating
                                 ? "Cannot publish until the first chapter is generated"
                                 : "Publish"
                           }
                        >
                           <EarthSvgIcon />
                        </button>
                        <span
                           style={styles.publishText(
                              showPublishDialog,
                              isFirstChapterGenerating
                           )}
                        >
                           {isFirstChapterGenerating
                              ? "generating..."
                              : "publish"}
                        </span>
                     </>
                  )}
               </div>

               {/* Options button (three dots) with dropdown menu */}
               <div ref={menuRef} style={{ position: "relative" }}>
                  <button
                     onClick={handleMenuToggle}
                     style={styles.optionsButton}
                  >
                     •••
                  </button>

                  {/* Options dropdown menu */}
                  {showMenu && (
                     <div style={styles.menuContainer}>
                        <button
                           onClick={handleDeleteClick}
                           style={styles.menuButton}
                           onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor =
                                 styles.menuButtonHover.backgroundColor;
                           }}
                           onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor =
                                 "transparent";
                           }}
                        >
                           Delete
                        </button>
                     </div>
                  )}
               </div>
            </div>

            {/* Publish confirmation dialog */}
            {showPublishDialog && (
               <div style={styles.publishDialogContainer}>
                  {publishSuccess ? (
                     <div style={styles.publishSuccessContainer}>
                        <div style={styles.publishSuccessIcon}>✓</div>
                        <p style={styles.publishSuccessText}>
                           Successfully published to community!
                        </p>
                     </div>
                  ) : (
                     <>
                        <p style={styles.publishDialogTitle}>
                           <strong>Share with the community?</strong>
                        </p>
                        <p style={styles.publishDialogText}>
                           Publishing this audiobook will make it available for
                           all users to listen to. This action cannot be undone.
                        </p>

                        {/* Error message if any */}
                        {publishError && (
                           <div style={styles.publishErrorMessage}>
                              {publishError}
                           </div>
                        )}

                        <div style={{ marginTop: "0.5rem" }}>
                           <button
                              onClick={() => onPublishDialogToggle(null)} // Close dialog using parent callback
                              style={styles.cancelButton}
                           >
                              Cancel
                           </button>
                           <button
                              onClick={handlePublish}
                              style={styles.confirmButton}
                           >
                              Publish
                           </button>
                        </div>
                     </>
                  )}
               </div>
            )}

            {/* DELETE CONFIRMATION MODAL */}
            {showDeleteConfirm && (
               <div style={styles.modalOverlay}>
                  <div ref={deleteModalRef} style={styles.modalContainer}>
                     <div style={styles.modalIconContainer}>
                        <div style={styles.modalIconCircle}>
                           <span style={styles.modalIcon}>!</span>
                        </div>
                        <h3 style={styles.modalTitle}>Delete Audiobook</h3>
                     </div>

                     <p style={styles.modalText}>
                        Are you sure you want to delete "{book.title}"? This
                        action cannot be undone.
                     </p>

                     <div style={styles.modalButtonContainer}>
                        <button
                           onClick={cancelDelete}
                           style={styles.modalCancelButton}
                        >
                           Cancel
                        </button>
                        <button
                           onClick={confirmDelete}
                           style={styles.modalDeleteButton}
                        >
                           Delete
                        </button>
                     </div>
                  </div>
               </div>
            )}
         </div>
      );
   },

   // Custom comparison function for React.memo
   (prevProps, nextProps) => {
      // Re-render if UUID changes (different book)
      if (prevProps.book.uuid !== nextProps.book.uuid) return false;

      // Re-render if showPublishDialog state changes
      if (prevProps.showPublishDialog !== nextProps.showPublishDialog)
         return false;

      // Re-render if theme changes
      if (prevProps.theme !== nextProps.theme) return false;

      // Re-render if important metadata changes
      if (
         prevProps.book.num_generated_chapters !==
         nextProps.book.num_generated_chapters
      )
         return false;
      if (
         prevProps.book.num_total_chapters !== nextProps.book.num_total_chapters
      )
         return false;
      if (prevProps.book.generation_status !== nextProps.book.generation_status)
         return false;
      if (prevProps.book.is_public !== nextProps.book.is_public) return false;
      if (prevProps.book.title !== nextProps.book.title) return false;

      // Don't re-render for other changes
      return true;
   }
);

export function AudiobookLibrary() {
   const { theme } = useTheme(); // Get current theme
   const styles = getStyles(theme); // Get styles based on theme

   const [library, setLibrary] = useState([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
   const [activePublishItemId, setActivePublishItemId] = useState(null);
   const token = localStorage.getItem("token");
   const isMountedRef = useRef(true);
   const intervalIdRef = useRef(null);

   // Get the audio context for playing audiobooks
   const audioContext = useContext(AudioContext);

   // Function to fetch library data - Same as original
   const fetchLibrary = async () => {
      if (!isMountedRef.current) return;

      try {
         if (!token) {
            console.error("No authentication token found.");
            setError("Authentication token is missing. Please log in again.");
            setLoading(false);
            return;
         }

         // Only show loading on initial fetch
         if (library.length === 0) {
            setLoading(true);
         }

         console.log(
            "Fetching library data with token:",
            token.substring(0, 10) + "..."
         );

         const res = await fetch("/api/user/library", {
            headers: {
               Authorization: `Bearer ${token}`,
               "Cache-Control": "no-cache", // Add no-cache to prevent stale data
            },
         });

         if (!isMountedRef.current) return;

         if (res.ok) {
            const data = await res.json();
            console.log("Library API response:", data);

            // Handle both null and array responses as valid
            if (Array.isArray(data)) {
               setLibrary(data);
               setError(null);
            } else if (data === null) {
               // Treat null as an empty array
               console.log("API returned null, treating as empty library");
               setLibrary([]);
               setError(null); // No error, just an empty library
            } else {
               console.warn("API returned invalid format:", data);
               setLibrary([]);
               setError(
                  "API response format error: Expected an array of library items"
               );
            }
         } else {
            const errorText = await res.text();
            console.error(
               `Failed to fetch library: ${res.status} - ${errorText}`
            );
            setError(
               `Failed to load library items (${res.status}). ${errorText}`
            );
         }
      } catch (error) {
         console.error("Error fetching library:", error);
         setError(`Network or server error: ${error.message}`);
      } finally {
         if (isMountedRef.current) {
            setLoading(false);
         }
      }
   };

   // Handle visibility changes and setup refresh interval
   useEffect(() => {
      console.log("AudiobookLibrary component mounted");
      isMountedRef.current = true;

      // Initial fetch when component mounts
      fetchLibrary();

      // Start the refresh interval
      startRefreshInterval();

      // Handle visibility changes
      const handleVisibilityChange = () => {
         if (document.visibilityState === "visible") {
            console.log(
               "Tab is visible - fetching fresh data and starting interval"
            );
            fetchLibrary(); // Immediately fetch data when tab becomes visible
            startRefreshInterval();
         } else {
            console.log("Tab is hidden - clearing interval");
            clearRefreshInterval();
         }
      };

      // Function to start refresh interval
      function startRefreshInterval() {
         // Clear any existing interval first
         clearRefreshInterval();

         // Set new interval
         intervalIdRef.current = setInterval(() => {
            console.log("Auto-refreshing library data...");
            fetchLibrary();
         }, 30000);
      }

      // Function to clear refresh interval
      function clearRefreshInterval() {
         if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
         }
      }

      // Add visibility change listener
      document.addEventListener("visibilitychange", handleVisibilityChange);

      // Cleanup on unmount
      return () => {
         console.log("Component unmounting - cleaning up");
         isMountedRef.current = false;
         document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange
         );
         clearRefreshInterval();
      };
   }, []); // Empty dependency array - only run on mount/unmount

   const handleRemove = async (uuid) => {
      if (!isMountedRef.current) return;

      try {
         const res = await fetch(`/api/user/library/${uuid}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
         });

         if (res.ok && isMountedRef.current) {
            // Re-fetch the library after successful removal
            fetchLibrary();
         }
      } catch (error) {
         console.error("Error removing audiobook:", error);
      }
   };

   // Handle publishing of books - No popup notification
   const handlePublish = async (uuid) => {
      if (!isMountedRef.current) return;

      // Update the book's status in the local state for immediate UI feedback
      setLibrary((prevLibrary) =>
         prevLibrary.map((book) =>
            book.uuid === uuid ? { ...book, is_public: true } : book
         )
      );
   };

   // Handle when a book's publish dialog should be shown
   const handlePublishDialog = (uuid) => {
      // If this item is already active, close it. Otherwise, open this one and close any others
      setActivePublishItemId(activePublishItemId === uuid ? null : uuid);
   };

   // Enhanced function to handle listening to an audiobook with explicit progress saving
   const handleListen = async (book) => {
      if (!audioContext || !audioContext.loadBook) {
         console.error("Audio context or loadBook function not available");
         return;
      }

      console.log(`🎧 Library: Starting to play audiobook: ${book.title}`);

      // First, force save the current progress if we have a book playing
      if (audioContext.currentBook && audioContext.forceSaveProgress) {
         console.log(
            `📋 Library: Forcing save for current book before switching`
         );
         try {
            // Wait for force save to complete
            const saved = await audioContext.forceSaveProgress();
            console.log(
               `📋 Library: Force save result: ${saved ? "Success" : "Failed"}`
            );
         } catch (err) {
            console.error("📋 Library: Error during force save:", err);
         }
      } else {
         console.log(
            `📋 Library: No current book to save or forceSaveProgress not available`
         );
      }

      // Brief delay to ensure saving completes
      console.log(`📋 Library: Waiting before loading new book...`);
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Now load the new book
      console.log(`📋 Library: Now loading book: ${book.title}`);

      // FIX: Use the book's current chapter instead of hardcoding to 1
      const startChapter = book.current_chapter || 1;
      console.log(`📋 Library: Starting from chapter ${startChapter}`);

      audioContext.loadBook(book, startChapter, true);
   };

   // Method to handle when a book is selected for generation
   const handleBookSelected = (book) => {
      console.log(
         `Book selected for generation from library: ${book.title} (${book.uuid})`
      );

      // No longer need to navigate or store in sessionStorage since
      // the AudiobookPromptSelector now handles generation internally
      console.log("Processing generation in-place without navigation");
   };

   return (
      <div style={styles.container}>
         <AudiobookPromptSelector onBookSelected={handleBookSelected} />
         <h2 className="inter-font" style={styles.pageTitle}>
            Your Stories...
         </h2>

         {/* Show loading state below the header */}
         {loading && library.length === 0 ? (
            <div style={styles.loadingContainer}>
               <p style={styles.loadingText}>Loading your audiobooks...</p>
            </div>
         ) : error ? (
            <div style={styles.errorContainer}>
               <p style={styles.errorText}>
                  <strong>Error:</strong> {error}
               </p>
               <button onClick={fetchLibrary} style={styles.tryAgainButton}>
                  Try Again
               </button>
            </div>
         ) : library && library.length > 0 ? (
            <div>
               {[...library].reverse().map((item) => (
                  <LibraryItem
                     key={item.uuid}
                     book={item}
                     onRemove={handleRemove}
                     onPublish={handlePublish}
                     onListen={handleListen}
                     showPublishDialog={activePublishItemId === item.uuid}
                     onPublishDialogToggle={handlePublishDialog}
                     theme={theme}
                  />
               ))}
            </div>
         ) : (
            <div style={styles.emptyLibraryContainer}>
               <div style={styles.emptyIconContainer}>
                  <span style={styles.emptyIcon}>📚</span>
               </div>
               <p style={styles.emptyTitle}>Your library is empty</p>
               <p style={styles.emptyText}>
                  Generate your first audiobook to get started
               </p>
            </div>
         )}
      </div>
   );
}
